/**
 * **What happens when a session ends** — [ADR-0007](../../../../docs/adr/0007-a-revoked-session-ends-in-prodoc-too.md),
 * adopting FiveOX ADR-0079 and ADR-0041.
 *
 * An access token is a 15-minute RS256 JWT that every service verifies
 * *locally* against JWKS. Nothing asks whether the session behind it still
 * exists. So when somebody in the organisation signs out of another FiveOX app
 * — which revokes the whole login group, ours included — **nothing this app
 * can see changes**: the stored token still verifies, every request still
 * succeeds, and a ProDoc tab that was closed at that moment reopens from
 * `localStorage` and carries on. That is the report the platform fixed this
 * against, verbatim: *"logout from one app, still logged in another"*.
 *
 * Four things have to be true, and this module is the one place that holds
 * all four:
 *
 * 1. **We ask.** `session.watch()` is the shell's watchdog (SESSION_WATCHDOG
 *    §3a): `GET /session/status` on start, on `focus`, on a bfcache restore
 *    and every 60s, coalesced to one call per 5s. A 401/403 ends the session;
 *    a 5xx, a timeout or a thrown fetch does **not** — auth being unavailable
 *    must never sign somebody out mid-upload, which is the worse bug of the two.
 * 2. **We listen, scoped.** The ~2s sign-out broadcast is estate-wide and the
 *    prototype never registered for it: there was no `onSignOut(...)` anywhere
 *    in its `src`. This app does — with its own user id, so that another app
 *    tidying up somebody *else's* dead session does not sign this person out.
 * 3. **We forget the organisation.** See `clearsTheOrgOnEnd` below.
 * 4. **We forget the passwords** (W22, FR-58, D56). A PDF's password, kept in
 *    this tab's memory after it unlocked a reading so its pages draw in the
 *    browser (`passwordKeeper`), belongs to the person who typed it and the
 *    organisation it was typed in. It goes when the session ends and when the
 *    organisation changes — whichever way either happens: the account menu,
 *    another tab's sign-out, the watchdog, the org switcher or Go To. The
 *    upload provider clears it too before a switch or sign-out it asked about;
 *    this is the floor under every other route.
 *
 * The watchdog itself is emphatically **not** copied here.
 * [`lib/session.ts`](session.ts) is `createSessionStore()`, so `watch()` is
 * already a method on the store we use (ADR-0007 §1).
 */

import { passwordKeeper } from '@fiveox/prodoc-ui'
import { onSignOut } from '@fiveox/shell'

import { setOrgId, subscribeOrg } from './api'
import { session } from './session'

/**
 * **The organisation does not outlive the session that chose it** (ADR-0007 §3).
 *
 * The platform clears the *shared parent-domain* org cookie inside
 * `broadcastSignOut()`, which fixed this for the core apps. **We are outside
 * that fix**, because ProDoc keeps its own per-app key (`fiveox.prodoc_org`,
 * `orgs.ts`) — deliberately, since storage is per-origin and one app's
 * selection must never be revived from another's.
 *
 * `resolveSelected()` already honours a remembered org *only if the user is
 * still a member of it*, so the next person to sign in on this browser is
 * never handed an organisation they do not belong to. Two things survive that
 * check, and for a document service they bite harder than anywhere else:
 *
 * - **The window before the orgs are fetched.** `api.ts` seeds its
 *   module-level `currentOrgId` from this key at import time, so every request
 *   the first paint makes carries `X-Fiveox-Org: <the previous person's org>`
 *   under the new person's token. The service fails closed — a cross-tenant
 *   read is `NOT_FOUND`, never `FORBIDDEN` (constitution §3) — so no document
 *   crosses. What is left behind is a **read-log row attributed to the wrong
 *   organisation** (constitution §4), in a trail that exists precisely to be
 *   produced to an auditor.
 * - **Two people who share an org**, which the membership check waves through
 *   by design. A and B both belong to the same practice and to a second one;
 *   A leaves the first selected, B signs in and lands in A's selection. A
 *   shared workstation in an accounts office is the ordinary case.
 *
 * **On the transition, not on the state.** Clearing whenever the session is
 * merely *absent* would also fire during the SSO round trip, when `/callback`
 * renders with no session for a moment before it stores the tokens — and would
 * quietly delete the remembered org on every single sign-in, which
 * `resolveSelected` exists to honour. So this fires only when a session that
 * **existed** has gone.
 *
 * **Through `setOrgId`, not `setSelectedOrgId`.** The key is only half of it:
 * `api.ts` also holds the org in a module-level variable that stamps the
 * header. Clearing the storage alone would leave this page's requests carrying
 * the old org for the rest of its life.
 */
function clearsTheOrgOnEnd(): () => void {
  let had = session.getSession() !== null
  return session.subscribe(() => {
    const has = session.getSession() !== null
    if (had && !has) {
      passwordKeeper.clear()
      setOrgId(null)
    }
    had = has
  })
}

/**
 * **A password never follows anyone into another organisation** (D56): every
 * change of the selected organisation — the switcher, Go To, a hand-over in
 * the address — forgets what this tab kept. `setOrgId` notifies only on a real
 * change, so settling on the same organisation again forgets nothing.
 */
function forgetsPasswordsOnSwitch(): () => void {
  return subscribeOrg(() => passwordKeeper.clear())
}

/**
 * Arm all four. Mounted **once**, from the guard that already owns the
 * *no session → SSO* decision; the returned unsubscribe is what an effect's
 * cleanup wants.
 *
 * Nothing here navigates. `RequireSession` reads the session through
 * `useSyncExternalStore`, so ending it re-renders the app into `SsoRedirect`
 * on its own — and that is the authoritative path, because accounts, with a
 * live session, is what actually decides who this is.
 */
export function armSessionEnd(): () => void {
  const stopWatchdog = session.watch()
  const stopOrgClear = clearsTheOrgOnEnd()
  const stopPasswordClear = forgetsPasswordsOnSwitch()
  // `forUser` is read at the moment a broadcast arrives, never captured: a
  // refresh rotates the session and a captured id would be stale.
  const stopListener = onSignOut(
    () => session.clearSession(),
    () => session.getSession()?.userId ?? null,
  )
  return () => {
    stopWatchdog()
    stopOrgClear()
    stopPasswordClear()
    stopListener()
  }
}
